import { t } from 'i18next';
import IconButton from '@standardsdigital/ui-components/IconButton';
import ConfirmationDialog from '@standardsdigital/ui-components/ConfirmationDialog';
import Tooltip from '@standardsdigital/ui-components/Tooltip';
import type {
  CellContext,
  ColumnHelper,
} from '@standardsdigital/ui-components/Table';
import type {
  ClassifierDtos,
  ServiceLevelDefinitionDto,
} from 'redux/services/generate/configurations/serviceLevelDefinitions';
import { fromNoYesEnum, getBitEnumNames } from '../serviceLevelOptions';
import styles from './ServiceLevelsDashboardPage.module.css';

const formatEnumList = (
  value: number,
  classifiers: ClassifierDtos | undefined,
  classifierKey: keyof ClassifierDtos,
) => {
  const names = getBitEnumNames(value, classifiers?.[classifierKey] as never);
  return names.length > 0 ? names.join(', ') : t('serviceLevels.none');
};

const formatServices = (
  serviceLevel: ServiceLevelDefinitionDto,
  classifiers: ClassifierDtos | undefined,
) => {
  const services = getBitEnumNames(
    serviceLevel.documentServices,
    classifiers?.documentServices,
  );

  if (fromNoYesEnum(serviceLevel.draftStandards)) {
    services.push(t('serviceLevels.serviceSettings.drafts'));
  }

  if (fromNoYesEnum(serviceLevel.redline)) {
    services.push(t('serviceLevels.serviceSettings.redline'));
  }

  if (serviceLevel.multiLanguage) {
    services.push(t('serviceLevels.serviceSettings.multiLanguage'));
  }

  services.push(formatPreviousVersions(serviceLevel.previousVersions));
  return services.join(', ');
};

const formatSubscriptionType = (
  subscriptionType: number | null | undefined,
  classifiers: ClassifierDtos | undefined,
) => {
  if (subscriptionType === null || subscriptionType === undefined) {
    return '';
  }

  return (
    classifiers?.subscriptionLicenseTypes?.find(
      (classifier) => classifier.enumValue === subscriptionType,
    )?.name ?? ''
  );
};

const formatPreviousVersions = (previousVersions: number) => {
  switch (previousVersions) {
    case 0:
      return t('serviceLevels.previousVersions.no');
    case 1:
      return t('serviceLevels.previousVersions.fromSubscriptionDate');
    case 2:
      return t('serviceLevels.previousVersions.allVersions');
    default:
      return String(previousVersions);
  }
};

const renderNameCell = (cell: CellContext<ServiceLevelDefinitionDto, string>) => (
  <>
    {cell.getValue()}
    {cell.row.original.deleted ? (
      <span className={styles.softDeletedRowMarker} aria-hidden="true" />
    ) : null}
  </>
);

const renderActionsCell = (
  cell: CellContext<ServiceLevelDefinitionDto, number>,
  openEditServiceLevel: (serviceLevel: ServiceLevelDefinitionDto) => void,
  deleteServiceLevel: (serviceLevel: ServiceLevelDefinitionDto) => void,
) => {
  const serviceLevel = cell.row.original;

  return (
    <div className={styles.actionsCell}>
      <IconButton
        icon="Edit"
        size="small"
        onClick={() => openEditServiceLevel(serviceLevel)}
        layout="icon-only"
        variant="text"
      >
        {t('general.edit')}
      </IconButton>
      <ConfirmationDialog
        title={t('serviceLevels.deleteTitle')}
        description={t('serviceLevels.deleteDescription', {
          name: serviceLevel.serviceLevelName,
        })}
        closeButtonText={t('general.close')}
        cancelButtonText={t('general.cancel')}
        confirmButtonText={t('general.delete')}
        onConfirm={() => deleteServiceLevel(serviceLevel)}
        modal={false}
      >
        <IconButton icon="Trash" size="small" layout="icon-only" variant="text">
          {t('general.delete')}
        </IconButton>
      </ConfirmationDialog>
    </div>
  );
};

const renderPriceFactorCell = (
  cell: CellContext<ServiceLevelDefinitionDto, number | null | undefined>,
) => {
  const priceFactor = cell.getValue();

  if (priceFactor === null || priceFactor === undefined) {
    const triggerButton = (
      <IconButton layout="icon-only" icon="Info" size="small" variant="text">
        {t('serviceLevels.tableValues.priceFactorNotSetTooltip')}
      </IconButton>
    );

    return (
      <div className={styles.priceFactorNotSet}>
        <span>{t('serviceLevels.tableValues.notSet')}</span>
        <Tooltip
          triggerButton={triggerButton}
          content={t('serviceLevels.tableValues.priceFactorNotSetTooltip')}
        />
      </div>
    );
  }

  return priceFactor;
};

const getColumnDefinitions = (
  columnHelper: ColumnHelper<ServiceLevelDefinitionDto>,
  openEditServiceLevel: (serviceLevel: ServiceLevelDefinitionDto) => void,
  deleteServiceLevel: (serviceLevel: ServiceLevelDefinitionDto) => void,
  classifiers: ClassifierDtos | undefined,
) => [
  columnHelper.accessor('serviceLevelName', {
    header: t('serviceLevels.tableHeaders.name'),
    cell: renderNameCell,
    minSize: 130,
  }),
  columnHelper.accessor(
    (serviceLevel) =>
      formatSubscriptionType(serviceLevel.subscriptionLicenseType, classifiers),
    {
      id: 'subscriptionType',
      header: t('serviceLevels.tableHeaders.subscriptionType'),
      minSize: 120,
    },
  ),
  columnHelper.accessor(
    (serviceLevel) => formatServices(serviceLevel, classifiers),
    {
      id: 'services',
      header: t('serviceLevels.tableHeaders.services'),
      minSize: 220,
      meta: {
        columnSize: '40%',
      },
    },
  ),
  columnHelper.accessor('languages', {
    header: t('serviceLevels.tableHeaders.languages'),
    cell: (info) => formatEnumList(info.getValue(), classifiers, 'languages'),
    minSize: 120,
    meta: {
      columnSize: '20%',
    },
  }),
  columnHelper.accessor('allowedNsbIds', {
    header: t('serviceLevels.tableHeaders.sdos'),
    cell: (info) => info.getValue().join(', '),
    minSize: 140,
    meta: {
      columnSize: '40%',
    },
  }),
  columnHelper.accessor((serviceLevel) => serviceLevel.priceFactor, {
    id: 'priceFactor',
    header: t('serviceLevels.tableHeaders.priceFactor'),
    cell: renderPriceFactorCell,
    minSize: 142,
  }),
  columnHelper.accessor('recId', {
    header: t('serviceLevels.tableHeaders.actions'),
    cell: (info) =>
      renderActionsCell(info, openEditServiceLevel, deleteServiceLevel),
    minSize: 105,
  }),
];

export default getColumnDefinitions;
